/**
 * Chapter018.js — Plant & Equipment Regular Checklist (OHSE 018)
 * Migrated from SWMS Platform Chapter018.tsx → JavaScript
 *
 * Props:
 *   data        {object}  equipment_periodic_check from swmsData
 *   onChange    {fn}      (newData) => void
 *   companyName {string}  from companyInfo.name
 */

import { useCallback } from 'react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';

const DEFAULT_CHECKLIST_ITEMS = [
  { description: 'Risk assessment', yes: false, no: false, na: false },
  { description: "Operator's manual", yes: false, no: false, na: false },
  { description: 'Maintenance reports', yes: false, no: false, na: false },
  { description: 'Logbook', yes: false, no: false, na: false },
  { description: 'Competency ticket/licence of operator', yes: false, no: false, na: false },
  { description: 'Fire extinguisher', yes: false, no: false, na: false },
  { description: 'Crack test reports', yes: false, no: false, na: false },
  { description: 'Chains tested and tagged', yes: false, no: false, na: false },
  { description: 'Regulatory Authority plant registration', yes: false, no: false, na: false },
  { description: 'Flashing light', yes: false, no: false, na: false },
  { description: 'Forward/reverse beeper', yes: false, no: false, na: false },
  { description: 'Tested and tagged electrically', yes: false, no: false, na: false },
  { description: 'Seat belt', yes: false, no: false, na: false },
  { description: 'Roll over Protection (ROPS)', yes: false, no: false, na: false },
];

const DEFAULT_EQUIPMENT_TYPES = [
  { category: 'Scissor Lifts / Boom Lifts', items: '' },
  { category: "Forklifts / Manatou's", items: '' },
  { category: 'Concrete Pumps', items: '' },
  { category: 'Excavators / Backhoes / Bob Cats', items: '' },
  { category: 'Cranes', items: '' },
  { category: 'Other', items: '' },
];

export default function Chapter018({ data = {}, onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const checklistItems = (data.checklist_items?.length === DEFAULT_CHECKLIST_ITEMS.length)
    ? data.checklist_items
    : DEFAULT_CHECKLIST_ITEMS;

  const equipmentTypes = (data.equipment_types?.length === DEFAULT_EQUIPMENT_TYPES.length)
    ? data.equipment_types
    : DEFAULT_EQUIPMENT_TYPES;

  // ── Header field update ──
  const updateHeaderField = useCallback((field, value) => {
    onChange({ ...data, [field]: value });
  }, [data, onChange]);

  // ── Checklist tri-state update ──
  const updateChecklistItem = useCallback((idx, field, value) => {
    const items = checklistItems.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      // Mutual exclusion among yes/no/na
      if (value) {
        if (field !== 'yes') updated.yes = false;
        if (field !== 'no') updated.no = false;
        if (field !== 'na') updated.na = false;
      }
      return updated;
    });
    onChange({ ...data, checklist_items: items });
  }, [data, onChange, checklistItems]);

  // ── Signature/provider field update ──
  const updateField = useCallback((field, value) => {
    onChange({ ...data, [field]: value });
  }, [data, onChange]);

  // ── Equipment type items update ──
  const updateEquipmentType = useCallback((idx, value) => {
    const types = equipmentTypes.map((et, i) => i === idx ? { ...et, items: value } : et);
    onChange({ ...data, equipment_types: types });
  }, [data, onChange, equipmentTypes]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 018</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Plant &amp; Equipment Regular Checklist
        </h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        The following checklist is completed by{' '}
        <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
        as a general and regular check on plant operation at the workplace.
      </div>

      {/* Checklist Section */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
            Plant and Equipment Checklist
          </h3>
        </div>
        <div className="p-4">
          {/* Header fields */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-6 pb-5 border-b border-[#e0dbd0]">
            <Field label="Service Provider Name">
              <input type="text" value={data.service_provider_name || ''} placeholder="Service provider name"
                onChange={e => updateHeaderField('service_provider_name', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Plant Type / Make">
              <input type="text" value={data.plant_type_make || ''} placeholder="e.g. Cat 320 Excavator"
                onChange={e => updateHeaderField('plant_type_make', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Plant No.">
              <input type="text" value={data.plant_no || ''} placeholder="Plant number"
                onChange={e => updateHeaderField('plant_no', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Serial No.">
              <input type="text" value={data.serial_no || ''} placeholder="Serial number"
                onChange={e => updateHeaderField('serial_no', e.target.value)} className={inputCls} />
            </Field>
          </div>

          {/* Checklist table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-[#1a1a2e]">
                  <th className="text-left py-2.5 px-3 font-semibold text-[#1a1a2e]">Description</th>
                  <th className="text-center py-2.5 px-3 font-semibold text-[#1a1a2e] w-[180px]">Check</th>
                </tr>
              </thead>
              <tbody>
                {checklistItems.map((item, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-[#faf9f6]' : 'bg-white'}>
                    <td className="py-2.5 px-3 align-middle text-xs text-[#1a1a2e]/80 leading-relaxed">
                      {item.description}
                    </td>
                    <td className="py-2.5 px-3 align-middle">
                      <div className="flex items-center justify-center gap-3">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="checkbox" checked={item.yes}
                            onChange={e => updateChecklistItem(idx, 'yes', e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-[#e0dbd0] cursor-pointer" />
                          <span className="text-[11px] font-semibold text-[#1a1a2e]">Yes</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="checkbox" checked={item.no}
                            onChange={e => updateChecklistItem(idx, 'no', e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-[#e0dbd0] cursor-pointer" />
                          <span className="text-[11px] font-semibold text-[#1a1a2e]">No</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="checkbox" checked={item.na}
                            onChange={e => updateChecklistItem(idx, 'na', e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-[#e0dbd0] cursor-pointer" />
                          <span className="text-[11px] font-semibold text-[#1a1a2e]">N/A</span>
                        </label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Plant Provider */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Plant Provider</h3>
        </div>
        <div className="p-4 grid grid-cols-3 gap-4">
          <Field label="Signature (type name)">
            <input type="text" value={data.plant_provider_signature || ''} placeholder="Signature"
              onChange={e => updateField('plant_provider_signature', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Name">
            <input type="text" value={data.plant_provider_name || ''} placeholder="Full name"
              onChange={e => updateField('plant_provider_name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Date">
            <input type="date" value={data.plant_provider_date || ''}
              onChange={e => updateField('plant_provider_date', e.target.value)} className={inputCls} />
          </Field>
        </div>
      </div>

      {/* Inspection Verified By */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Inspection Verified By</h3>
        </div>
        <div className="p-4 grid grid-cols-3 gap-4">
          <Field label="Signature (type name)">
            <input type="text" value={data.verified_by_signature || ''} placeholder="Signature"
              onChange={e => updateField('verified_by_signature', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Name">
            <input type="text" value={data.verified_by_name || ''} placeholder="Full name"
              onChange={e => updateField('verified_by_name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Date">
            <input type="date" value={data.verified_by_date || ''}
              onChange={e => updateField('verified_by_date', e.target.value)} className={inputCls} />
          </Field>
        </div>
      </div>

      {/* Relevant Aspects Considered */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Relevant Aspects Considered</h3>
        </div>
        <div className="p-4">
          <p className="text-sm text-[#1a1a2e] leading-relaxed mb-4">
            In undertaking regular checks of plant and equipment,{' '}
            <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
            includes consideration of relevant aspects as follows:
          </p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            {equipmentTypes.map((et, idx) => (
              <Field key={idx} label={et.category}>
                <input type="text" value={et.items} placeholder={`Details for ${et.category}`}
                  onChange={e => updateEquipmentType(idx, e.target.value)} className={inputCls} />
              </Field>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
